import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { PMDialog } from "../../../utils/PMDialog";
import { EmployeePickerSheet } from "../../hours/hourFormPickers";
import { getUsersAsync } from "../../../../slices/user";
import { createEvaluationTiltak, evaluationApiError } from "../../../../api/hse/evaluations";
import { EvaluationLabelInfo } from "./EvaluationLabelInfo";
import { HseDialogFooter } from "../HseDialogFooter";
import { TILTAK_ACTION_TYPES, personLabel, tiltakCreatePath } from "./evaluationUtils";

const inputClass =
  "w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-900 outline-none focus:border-[#1463C7]";

export function CreateTiltakDialog({ open, onClose, evaluation, finding, onCreated }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users?.data || []);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    action_type: "generic",
    responsible_user_id: "",
    deadline: "",
  });

  useEffect(() => {
    if (open) {
      dispatch(getUsersAsync({ filters: [{ name: "EMPLOYEE", value: true }] }));
      setForm({
        title: finding?.title || evaluation?.title || "",
        description: finding?.description || evaluation?.findings || "",
        action_type: "generic",
        responsible_user_id: evaluation?.responsible_user_id || "",
        deadline: "",
      });
    }
  }, [open, finding, evaluation, dispatch]);

  const responsible = users.find((user) => String(user.id) === String(form.responsible_user_id));
  const deepLink = tiltakCreatePath({
    evaluationId: evaluation?.id,
    title: form.title || finding?.title,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error(t("hse.evaluation.tiltak.title_required"));
      return;
    }
    if (!form.deadline) {
      toast.error(t("hse.evaluation.tiltak.deadline_required"));
      return;
    }
    if (!form.responsible_user_id) {
      toast.error(t("hse.evaluation.tiltak.responsible_required"));
      return;
    }
    try {
      setSaving(true);
      const created = await createEvaluationTiltak(evaluation.id, {
        title: form.title.trim(),
        description: form.description,
        action_type: form.action_type,
        deadline: form.deadline,
        responsible_user_id: form.responsible_user_id,
      });
      toast.success(t("hse.evaluation.tiltak.created"));
      onCreated?.(created);
      onClose();
    } catch (error) {
      toast.error(evaluationApiError(error) || t("hse.evaluation.tiltak.create_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("hse.evaluation.tiltak.dialog_title")}
      size="lg"
      closeOnOutsideClick
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-slate-500">
          <EvaluationLabelInfo label={t("hse.evaluation.tiltak")} infoKey="tiltak" />
          {" — "}
          {t("hse.evaluation.tiltak.dialog_hint")}
        </p>
        <label className="block">
          <span className="mb-2 block text-[13px] font-bold text-slate-700">
            {t("hse.evaluation.fields.title")}
          </span>
          <input
            className={inputClass}
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
        <label className="block">
          <span className="mb-2 block text-[13px] font-bold text-slate-700">
            {t("hse.evaluation.fields.description")}
          </span>
          <textarea
            rows={3}
            className={inputClass}
            value={form.description}
            onChange={(event) =>
              setForm((current) => ({ ...current, description: event.target.value }))
            }
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-[13px] font-bold text-slate-700">
              {t("hse.evaluation.fields.action_type")}
            </span>
            <select
              className={inputClass}
              value={form.action_type}
              onChange={(event) =>
                setForm((current) => ({ ...current, action_type: event.target.value }))
              }
            >
              {TILTAK_ACTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`hse.evaluation.tiltak.types.${type}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-2 block text-[13px] font-bold text-slate-700">
              {t("hse.evaluation.fields.due_date")}
            </span>
            <input
              type="date"
              className={inputClass}
              value={form.deadline}
              onChange={(event) =>
                setForm((current) => ({ ...current, deadline: event.target.value }))
              }
            />
          </label>
          <div className="sm:col-span-2">
            <span className="mb-2 block text-[13px] font-bold text-slate-700">
              {t("hse.evaluation.fields.responsible")}
            </span>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className={`${inputClass} text-left`}
            >
              {responsible ? personLabel(responsible) : t("hse.evaluation.participants.choose_one")}
            </button>
            <EmployeePickerSheet
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              selectedId={form.responsible_user_id}
              onSelect={(user) =>
                setForm((current) => ({ ...current, responsible_user_id: user?.id || "" }))
              }
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <Link to={deepLink} className="text-sm font-semibold text-[#1463C7] hover:underline">
            {t("hse.evaluation.tiltak.open_page")}
          </Link>
          <HseDialogFooter
            onCancel={onClose}
            cancelLabel={t("hse.evaluation.form.cancel")}
            submitLabel={saving ? t("hse.evaluation.form.saving") : t("hse.evaluation.tiltak.create")}
            submitting={saving}
          />
        </div>
      </form>
    </PMDialog>
  );
}
